import React, { useState, useMemo, useCallback, memo } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import useProductAvailableSizes from 'data-hooks/useProductAvailableSizes';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useModularPDPAvailableSizes from 'data-hooks/useModularPDPAvailableSizes';
import useModularDetails from 'data-hooks/useModularDetails';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import PDPAvailableInSizesUI from './PDPAvailableInSizesUI';
import usePdpIsModularBYOProduct from '../state/context/usePdpIsModularBYOProduct';

const isAddOnProduct = lowercaseName =>
  lowercaseName.includes('add-on') ||
  lowercaseName.includes('add on') ||
  lowercaseName.includes('addon');

const PDPAvailableInSizesInner = ({ slug }) => {
  // Any products that are unavailable for purchase should not be shown
  const [unavailableProductIds, setUnavailableProductIds] = useState([]);

  const { state: routerState } = useLocation();
  const routerHasProductData = routerState?.productsData?.length;

  const {
    loading: basicDetailsLoading,
    data: basicDetails,
  } = usePDPBasicDetails();

  const { loading: sizesLoading, data } = useProductAvailableSizes();
  const modularAvailableSizesData = useModularPDPAvailableSizes();
  const modularDetails = useModularDetails(slug);
  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();

  const loading =
    basicDetailsLoading ||
    (!pdpIsModularBYOProduct && sizesLoading) ||
    (pdpIsModularBYOProduct && modularAvailableSizesData.loading);

  const unfilteredProductsAndIds = useMemo(() => {
    if (
      !!modularDetails?.data?.isModularPreConfigProduct &&
      !!modularDetails?.data?.preConfigProducts?.length &&
      !pdpIsModularBYOProduct
    ) {
      let modularIds = '';
      const updatedProducts = modularDetails?.data.preConfigProducts
        .filter(prod => !!prod && prod.id !== basicDetails?.id)
        .filter(({ name }) => {
          const lowercaseName = name?.toLowerCase() || '';
          return !!lowercaseName && !isAddOnProduct(lowercaseName);
        })
        .map(({ id, slug: productSlug }) => {
          if ((unavailableProductIds || []).includes(id)) return null;
          modularIds += `${id},`;
          return {
            id,
            slug: productSlug,
          };
        })
        .filter(Boolean);

      return { unfilteredProducts: updatedProducts, productIds: modularIds };
    }

    if (
      !!data?.available_sizes &&
      !pdpIsModularBYOProduct &&
      !loading &&
      !modularDetails?.loading &&
      !modularDetails?.data?.isModularPreConfigProduct
    ) {
      let productIdsStr = '';
      const updatedProducts = data.available_sizes
        .filter(Boolean)
        .filter(({ name }) => {
          const lowercaseName = name?.toLowerCase() || '';
          return !!lowercaseName && !isAddOnProduct(lowercaseName);
        })
        .map(({ id, slug: prodSlug }) => {
          if (
            (unavailableProductIds || []).includes(id) ||
            productIdsStr.includes(id)
          )
            return null;
          productIdsStr += `${id},`;
          return {
            id,
            slug: prodSlug,
          };
        })
        .filter(Boolean);

      return {
        unfilteredProducts: updatedProducts,
        productIds: productIdsStr,
      };
    }

    if (
      !!modularAvailableSizesData?.data?.available_sizes &&
      pdpIsModularBYOProduct &&
      !loading
    ) {
      let productIdsStr = '';
      const updatedProducts = modularAvailableSizesData.data.available_sizes
        .filter(Boolean)
        .filter(({ name }) => {
          const lowercaseName = name?.toLowerCase() || '';
          return !!lowercaseName && !isAddOnProduct(lowercaseName);
        })
        .map(({ id, slug: prodSlug }) => {
          if (
            (unavailableProductIds || []).includes(id) ||
            productIdsStr.includes(id)
          )
            return null;
          productIdsStr += `${id},`;
          return {
            id,
            slug: prodSlug,
          };
        })
        .filter(Boolean);

      return {
        unfilteredProducts: updatedProducts,
        productIds: productIdsStr,
      };
    }

    return {};
  }, [
    basicDetails?.id,
    data?.available_sizes,
    pdpIsModularBYOProduct,
    loading,
    modularAvailableSizesData?.data?.available_sizes,
    modularDetails?.data?.isModularPreConfigProduct,
    modularDetails?.data?.preConfigProducts,
    modularDetails?.loading,
    unavailableProductIds,
  ]);

  const { unfilteredProducts } = unfilteredProductsAndIds;

  const removeUnavailableProductId = useCallback(productId => {
    setUnavailableProductIds(prevUnavailableProductIds =>
      prevUnavailableProductIds.includes(productId)
        ? prevUnavailableProductIds
        : [...prevUnavailableProductIds, productId]
    );
  }, []);

  const filteredProducts = useMemo(() => {
    if (!unfilteredProducts?.length) {
      return unfilteredProducts;
    }

    const updatedFilteredProducts = unfilteredProducts.filter(
      product => !unavailableProductIds.includes(product.id)
    );

    if (updatedFilteredProducts.length) {
      return updatedFilteredProducts;
    }

    return unfilteredProducts;
  }, [unfilteredProducts, unavailableProductIds]);

  const buildYourOwnData = useMemo(() => {
    if (
      !!modularDetails?.data?.isModularPreConfigProduct &&
      !!modularDetails?.data?.preConfigProducts?.length &&
      !pdpIsModularBYOProduct
    ) {
      let finalSlug = basicDetails?.meta.collectionSlug
        ? basicDetails?.meta.collectionSlug.replace('collections/', '')
        : '';
      finalSlug = finalSlug.length
        ? `${finalSlug.charAt(0).toUpperCase()}${finalSlug.slice(1)}`
        : '';
      return {
        showBYOSize: true,
        buildYourOwnDetails: {
          name: finalSlug,
          urlKey: `/modular/${finalSlug.toLowerCase()}-modular/`,
          dimensions: `Choose from ${filteredProducts.length} pieces to make any configuration.`,
        },
      };
    }
    return { showBYOSize: false, buildYourOwnDetails: {} };
  }, [
    basicDetails?.meta?.collectionSlug,
    modularDetails?.data?.isModularPreConfigProduct,
    modularDetails?.data?.preConfigProducts?.length,
    pdpIsModularBYOProduct,
    filteredProducts,
  ]);

  const { showBYOSize, buildYourOwnDetails } = buildYourOwnData;

  if (!loading && !unfilteredProducts?.length) {
    return <div className="PDPAvailableInSizes__loading" />;
  }

  const { id } = pdpIsModularBYOProduct
    ? modularAvailableSizesData.data
    : data || {};

  return (
    <PDPAvailableInSizesUI
      buildYourOwnDetails={buildYourOwnDetails}
      currentProductId={id}
      initialIsExpanded={routerState?.initialIsExpanded}
      loading={!routerHasProductData && loading}
      pdpIsModularBYOProduct={pdpIsModularBYOProduct}
      products={
        routerHasProductData ? routerState?.productsData : filteredProducts
      }
      removeUnavailableProductId={removeUnavailableProductId}
      showBYOSize={showBYOSize}
    />
  );
};

PDPAvailableInSizesInner.propTypes = {
  slug: PropTypes.string,
};

const PDPAvailableInSizes = props => (
  <SuspenseWithLoader>
    <PDPAvailableInSizesInner {...props} />
  </SuspenseWithLoader>
);

export default memo(PDPAvailableInSizes);
